// Business drafts only. No login credentials, tokens, mail configuration or provider data belong here.
export interface SalesSubmissionResult {
  id: number
  requestId: string
  notification: { status: 'queued' | 'held' | 'pending' | 'old_hub'; reason?: string; emailId?: string }
}
export interface DraftStorage { getItem(key: string): string | null; setItem(key: string, value: string): void }
export type SubmissionPhase = 'draft' | 'sending' | 'rejected' | 'uncertain' | 'saved'
interface Failure { message: string; code: string }
export interface SubmissionState<Draft> {
  version: 1
  requestId: string
  values: Draft
  phase: SubmissionPhase
  captured: Record<string, unknown> | null
  result: SalesSubmissionResult | null
  failure: Failure | null
}
type Sender = (body: Record<string, unknown>) => Promise<SalesSubmissionResult>
const clone = <T>(v: T): T => structuredClone(v)
function freeze<T>(v: T): T {
  if (v && typeof v === 'object') { Object.freeze(v); for (const value of Object.values(v)) freeze(value) }
  return v
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
export const submissionStorageKey = (scope: string) => `briary.sales-submission.v1:${scope}`

export class SalesSubmissionDraft<Draft extends object> {
  private state: SubmissionState<Draft>
  private listeners = new Set<() => void>()
  private inFlight: Promise<SalesSubmissionResult> | null = null
  private uncertain = false
  constructor(private key: string, initial: Draft, private storage: DraftStorage, private newId = () => crypto.randomUUID() as string) {
    try {
      const raw = storage.getItem(key)
      if (raw) {
        const old = JSON.parse(raw) as SubmissionState<Draft>
        const validValues = old.values && Object.entries(initial).every(([k, v]) => k in old.values && (v === null ? old.values[k as keyof Draft] === null || typeof old.values[k as keyof Draft] === 'number' : typeof old.values[k as keyof Draft] === typeof v))
        if (old.version !== 1 || !uuid.test(old.requestId) || !validValues || !['draft', 'sending', 'rejected', 'uncertain', 'saved'].includes(old.phase)
          || (old.phase !== 'draft' && (!old.captured || old.captured.requestId !== old.requestId))
          || (old.phase === 'saved' && (!Number.isSafeInteger(old.result?.id) || !old.result?.notification))) throw new Error('Invalid retained submission')
        this.state = freeze({ ...old, phase: old.phase === 'sending' || (old.phase === 'draft' && !!old.captured) ? 'uncertain' : old.phase })
        this.uncertain = this.state.phase === 'uncertain'
      } else this.state = this.fresh(initial)
    } catch {
      // Never silently turn an unreadable previous attempt into a fresh send.
      this.state = freeze({ ...this.fresh(initial), phase: 'uncertain', failure: { code: 'draft_restore', message: 'Your previous request could not be restored. Please contact Briary before submitting it again.' } })
      this.uncertain = true
    }
  }
  private fresh(values: Draft): SubmissionState<Draft> {
    return freeze({ version: 1, requestId: this.newId(), values: clone(values), phase: 'draft', captured: null, result: null, failure: null })
  }
  snapshot = () => this.state
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener) } }
  private publish(state: SubmissionState<Draft>, persist = true) {
    this.state = freeze(state)
    if (persist) { try { this.storage.setItem(this.key, JSON.stringify(state)) } catch { /* send() checks durable storage before making a request */ } }
    for (const listener of this.listeners) listener()
  }
  edit(values: Draft) {
    if (this.state.phase !== 'draft') throw new Error('Resolve the original submission before changing its details.')
    this.publish({ ...this.state, values: clone(values), failure: null })
  }
  reviseRejected() {
    if (this.state.phase !== 'rejected' || this.inFlight) throw new Error('The original submission must be resolved first.')
    this.uncertain = false
    this.publish(this.fresh(this.state.values))
  }
  newDraft(values: Draft) {
    if (this.state.phase !== 'saved' || this.inFlight) throw new Error('Finish the original submission before starting another.')
    this.uncertain = false
    this.publish(this.fresh(values))
  }
  submit(prepare: (values: Draft) => Record<string, unknown>, sender: Sender): Promise<SalesSubmissionResult> {
    if (this.inFlight) return this.inFlight
    if (this.state.result) return Promise.resolve(this.state.result)
    if (!this.state.captured) {
      if (this.state.phase !== 'draft') return Promise.reject(new Error('Your previous request needs to be resolved first.'))
      const captured = { ...clone(prepare(this.state.values)), requestId: this.state.requestId }
      this.publish({ ...this.state, captured })
    }
    return this.send(sender)
  }
  retry(sender: Sender): Promise<SalesSubmissionResult> {
    if (this.inFlight) return this.inFlight
    if (!this.state.captured) return Promise.reject(new Error('Your previous request could not be restored.'))
    return this.send(sender)
  }
  private send(sender: Sender): Promise<SalesSubmissionResult> {
    const before = this.state
    const sending = { ...before, phase: 'sending' as const, failure: null }
    try { this.storage.setItem(this.key, JSON.stringify(sending)) }
    catch {
      this.publish({ ...before, phase: before.result ? 'saved' : 'rejected', failure: { code: 'draft_storage', message: 'Your browser could not keep this request for a safe retry. Please check your browser storage settings and try again.' } }, false)
      return Promise.reject(new Error('Draft storage unavailable'))
    }
    this.publish(sending, false)
    // Defer sender invocation until inFlight is assigned: two synchronous clicks share this exact promise.
    this.inFlight = Promise.resolve().then(() => sender(clone(before.captured!))).then((result) => {
      if (!result || !Number.isSafeInteger(result.id) || result.requestId !== before.requestId || !['queued', 'held', 'pending', 'old_hub'].includes(result.notification?.status)) throw new Error('Submission response could not be confirmed')
      this.uncertain = false
      this.publish({ ...this.state, result, phase: 'saved', failure: null })
      return result
    }).catch((error: unknown) => {
      const e = error as { status?: number; code?: string; message?: string }
      const conflict = ['submission_conflict', 'submission_id_mismatch'].includes(e.code || '')
      const rejected = !this.uncertain && !conflict && !!e.status && e.status >= 400 && e.status < 500
      this.uncertain ||= !rejected
      const idError = e.code?.startsWith('submission_id_')
      const message = before.result ? 'Your request is saved, but its email status could not be checked. Try checking again.'
        : conflict ? 'These details could not be matched to the earlier request. Please contact Briary before starting another.'
          : rejected ? (idError ? 'This request could not be submitted safely. Please try again or contact Briary.' : e.message || 'The request was not accepted. Please review the details.')
            : 'We could not confirm whether your request was saved. Your original details have been kept. Retry the original request before making changes.'
      this.publish({ ...this.state, phase: before.result ? 'saved' : rejected ? 'rejected' : 'uncertain', failure: { code: e.code || 'unconfirmed', message } })
      throw error
    }).finally(() => { this.inFlight = null; for (const listener of this.listeners) listener() })
    return this.inFlight
  }
}
